import { useCallback, useEffect, useState } from 'react';

import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';

/**
 * Simplified version of:
 * https://github.com/streamich/react-use/blob/master/src/useLocalStorage.ts
 */
const useLocalStorage = <T>(
  key: LocalStorageKey,
  initialValue: T,
  { sync }: { sync: boolean } = { sync: false },
): [T, (newData: ((prevState: T) => T) | T) => void] => {
  const [state, setInternalState] = useState<T>(() => {
    const localStorageValue = StorageClient.getLocalItem(key);
    if (localStorageValue != null) {
      return JSON.parse(localStorageValue) as T;
    } else {
      initialValue != null && StorageClient.setLocalItem(key, JSON.stringify(initialValue));
      return initialValue;
    }
  });

  // Detect changes on storage from other tabs
  useEffect(() => {
    if (!sync) return;

    const onStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue) {
        setInternalState(JSON.parse(e.newValue) as T);
      }
    };
    window.addEventListener('storage', onStorageChange);
    return () => {
      window.removeEventListener('storage', onStorageChange);
    };
  }, [key, sync]);

  const setState = useCallback(
    (newDataOrDispatcher: ((prevState: T) => T) | T) => {
      const newData =
        typeof newDataOrDispatcher === 'function'
          ? (newDataOrDispatcher as (prevState: T) => T)(state)
          : newDataOrDispatcher;
      const newValue = JSON.stringify(newData);
      StorageClient.setLocalItem(key, newValue);

      // In the same tab, `storage` event isn't triggred so that state may be stale for some situations.
      // See "Note" on https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
      // When `sync` is true we manually dispatch `storage` to notify within the same tab
      if (sync) {
        window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
      } else {
        setInternalState(newData);
      }
    },
    [key, state, sync],
  );

  return [state, setState];
};

export default useLocalStorage;
